import { ToggleButton, ToggleButtonGroup } from "@mui/material";
import type { Theme } from "@mui/material/styles";
import { useTheme } from "@mui/material/styles";
import type { JSX } from "react";

interface RentReferenceToggleOption<T extends string> {
  value: T;
  label: string;
}

interface RentReferenceToggleProps<T extends string> {
  value: T;
  options: RentReferenceToggleOption<T>[];
  ariaLabel: string;
  onChange?: (value: T) => void;
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const createStyles = (theme: Theme) => ({
  root: {
    height: "45px",
    padding: "4px",
    borderRadius: "76px",
    border: `1px solid ${theme.semanticColors.border.subtle}`,
    backgroundColor: "white",
    flexShrink: 0,
    gap: "4px",
    "& .MuiToggleButtonGroup-grouped": {
      margin: 0,
      border: `1px solid ${theme.palette.common.white}`,
      borderRadius: "41px !important",
    },
  },
  button: {
    height: "35px",
    px: "12px",
    py: "8px",
    border: `1px solid ${theme.palette.common.white} !important`,
    borderRadius: "41px",
    color: theme.semanticColors.text.primary,
    fontSize: theme.analysisLayout.demandScoreLabelFontSize,
    fontWeight: 600,
    lineHeight: "23px",
    textTransform: "none",
    "&.Mui-selected": {
      border: `1px solid ${theme.semanticColors.interactive.primary} !important`,
      backgroundColor: theme.semanticColors.surface.blueBg,
      color: theme.semanticColors.interactive.primary,
    },
    "&:hover": {
      backgroundColor: "transparent",
    },
    "&.Mui-selected:hover": {
      backgroundColor: theme.semanticColors.surface.blueBg,
    },
  },
});

export const RentReferenceToggle = <T extends string>({
  value,
  options,
  ariaLabel,
  onChange,
}: RentReferenceToggleProps<T>): JSX.Element => {
  const theme = useTheme();
  const styles = createStyles(theme);

  return (
    <ToggleButtonGroup
      exclusive
      value={value}
      aria-label={ariaLabel}
      sx={styles.root}
      onChange={(_event, nextValue: T | null) => {
        if (nextValue !== null) {
          onChange?.(nextValue);
        }
      }}
    >
      {options.map((option) => (
        <ToggleButton key={option.value} value={option.value} sx={styles.button}>
          {option.label}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );
};
